import { useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import api from '../utils/api';
import { ResendVerification } from '../components/RegisterPanel';

/**
 * The page a verification email links to: /verify-email?token=...
 *
 * It submits the token with a POST rather than the link doing the work
 * itself, because mail security scanners fetch every link in a message before
 * the person sees it; a link that verified on GET would be spent by the
 * scanner. Scanners do not run this script.
 *
 * Exactly ONE request per page load, even under React StrictMode's double
 * effect in development: the token is single-use, so a second request would
 * answer "already used" and overwrite a success the person just earned.
 *
 * The token is removed from the address bar as soon as it has been read, so it
 * does not linger in browser history or leak through a Referer header.
 */
export default function VerifyEmail() {
  const { t, i18n } = useTranslation('register');
  const { search } = useLocation();
  const token = new URLSearchParams(search).get('token') || '';
  const [state, setState] = useState(token ? 'verifying' : 'invalid');
  const [result, setResult] = useState(null);
  const started = useRef(false);

  useEffect(() => {
    if (!token || started.current) return;
    started.current = true;
    try {
      window.history.replaceState(null, '', window.location.pathname);
    } catch {
      // Not fatal: the token is single-use anyway.
    }
    api.post('/auth/verify-email', { token })
      .then((res) => { setResult(res.data); setState('verified'); })
      .catch((err) => {
        const status = err?.response?.status;
        setState(status === 410 ? 'expired' : status === 400 ? 'invalid' : 'failed');
      });
  }, [token]);

  const isPublic = result?.account_type === 'public';

  return (
    <div style={styles.page}>
      <div style={styles.card}>
        <div style={styles.langRow}>
          <button type="button" style={styles.langBtn} title={t('tip.language')}
                  onClick={() => i18n.changeLanguage(i18n.language === 'en' ? 'he' : 'en')}>
            {i18n.language === 'en' ? 'עב' : 'EN'}
          </button>
        </div>
        <h1 style={styles.title}>{t('verify.title')}</h1>

        {state === 'verifying' && <p role="status" style={styles.text}>{t('verify.verifying')}</p>}

        {state === 'verified' && (
          <div role="status" data-testid="verify-success">
            <p style={styles.success}>{t('verify.success')}</p>
            <p style={styles.text}>
              {result?.pending_approval
                ? t(isPublic ? 'verify.pendingPublic' : 'verify.pendingStaff')
                : t('verify.readyStaff')}
            </p>
            <Link to={isPublic ? '/survey' : '/login'} title={isPublic ? t('tip.goToSurvey') : t('tip.goToSignIn')} style={styles.link}>
              {t(isPublic ? 'verify.goToSurvey' : 'verify.goToSignIn')}
            </Link>
          </div>
        )}

        {state === 'expired' && (
          <div data-testid="verify-expired">
            <p role="alert" style={styles.problem}>{t('verify.expired')}</p>
            <p style={styles.text}>{t('verify.expiredHelp')}</p>
            <ResendVerification />
          </div>
        )}

        {state === 'invalid' && (
          <div data-testid="verify-invalid">
            <p role="alert" style={styles.problem}>{t('verify.invalid')}</p>
            <Link to="/login" title={t('tip.goToSignIn')} style={styles.link}>{t('verify.goToSignIn')}</Link>
          </div>
        )}

        {state === 'failed' && (
          <div data-testid="verify-failed">
            <p role="alert" style={styles.problem}>{t('verify.failed')}</p>
            <Link to="/login" title={t('tip.goToSignIn')} style={styles.link}>{t('verify.goToSignIn')}</Link>
          </div>
        )}
      </div>
    </div>
  );
}

const styles = {
  page: {
    minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
    background: 'linear-gradient(160deg, var(--c-primary-soft-3) 0%, var(--c-primary-soft-4) 50%, var(--c-primary-soft-5) 100%)', padding: 16,
  },
  card: {
    width: '100%', maxWidth: 440, background: 'var(--c-surface)', borderRadius: 16, padding: '28px 32px',
    boxShadow: '0 8px 40px rgba(44, 110, 73, 0.12), 0 2px 8px rgba(0,0,0,0.06)',
  },
  langRow: { display: 'flex', justifyContent: 'flex-end' },
  langBtn: {
    border: '1px solid var(--c-border-strong)', borderRadius: 6, background: 'var(--c-surface)',
    padding: '4px 10px', cursor: 'pointer', fontSize: 12,
  },
  title: { fontSize: 20, color: 'var(--c-primary-fg)', margin: '8px 0 16px' },
  text: { fontSize: 14, color: 'var(--c-text-body-fg)', lineHeight: 1.5 },
  success: { fontSize: 15, fontWeight: 600, color: 'var(--c-success-3-fg)' },
  problem: { fontSize: 15, fontWeight: 600, color: 'var(--c-danger-2-fg)' },
  link: { color: 'var(--c-primary-fg)', fontWeight: 600 },
};
